import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.click';

export default function ArticleDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <section className="pt-32 pb-16 px-4 md:px-10 text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-950 mb-4">Artikel nicht gefunden</h1>
          <p className="text-foreground-500 mb-6">Der gesuchte Artikel existiert leider nicht.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            {t('articleDetail.back_home')}
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Header */}
      <section className="pt-24 md:pt-28 pb-8 md:pb-12 px-4 md:px-10">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb Schema.org */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Startseite", "item": `${siteUrl}/` },
                { "@type": "ListItem", "position": 2, "name": "Artikel", "item": `${siteUrl}/` },
                { "@type": "ListItem", "position": 3, "name": article.title, "item": `${siteUrl}/article/${article.slug}` }
              ]
            })
          }} />

          {/* Article Schema.org */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": article.title,
              "description": article.description,
              "image": article.imageUrl,
              "datePublished": article.published,
              "dateModified": article.published,
              "author": { "@type": "Organization", "name": "Oopbuy Spreadsheet Redaktion" },
              "publisher": { "@type": "Organization", "name": "Oopbuy Spreadsheet", "url": siteUrl },
              "inLanguage": "de"
            })
          }} />

          {/* FAQ Schema.org */}
          {article.faqs.length > 0 && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": article.faqs.map((faq) => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
                }))
              })
            }} />
          )}

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="breadcrumb">
            <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">{t('articleDetail.breadcrumb_home')}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">{t('articleDetail.breadcrumb_articles')}</span>
          </nav>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">{article.category}</span>
            <span className="text-xs text-foreground-400">{article.readingTime} Lesezeit</span>
            <span className="text-xs text-foreground-400">{article.published}</span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            {article.title}
          </h1>

          <p className="text-sm text-foreground-500 mb-8">{t('articleDetail.updated')}</p>

          {/* Author */}
          <div className="flex items-center gap-3 pb-8 border-b border-background-200">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-accent-100 flex items-center justify-center">
              <i className="ri-user-line text-lg text-accent-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-800">Oopbuy Redaktionsteam</p>
              <p className="text-xs text-foreground-500">Spreadsheet-Experten</p>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="px-4 md:px-10 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-foreground-700 leading-relaxed space-y-8">

            {/* Featured Image */}
            <div className="rounded-2xl overflow-hidden aspect-[16/7] my-10">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                title={article.title}
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>

            {/* TOC */}
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-10">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4">{t('articleDetail.toc')}</h2>
              <ol className="space-y-2 text-sm text-foreground-600 list-decimal list-inside">
                {article.sections.map((section, i) => (
                  <li key={i}>{section.heading}</li>
                ))}
              </ol>
            </div>

            {/* Sections */}
            {article.sections.map((section, sIdx) => (
              <section key={sIdx}>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                  {sIdx + 1}. {section.heading}
                </h2>
                {section.content.map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base mb-4" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}

                {/* Comparison table */}
                {section.hasComparison && section.comparisonData && (
                  <div className="overflow-x-auto rounded-xl border border-background-200 mb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="text-left px-5 py-3 font-semibold text-foreground-800">Merkmal</th>
                          <th className="text-left px-5 py-3 font-semibold text-accent-700">Oopbuy Spreadsheet</th>
                          <th className="text-left px-5 py-3 font-semibold text-foreground-600">Alternative</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200">
                        {section.comparisonData.map((row, rIdx) => (
                          <tr key={rIdx} className="hover:bg-background-50">
                            <td className="px-5 py-3 text-foreground-700">{row.feature}</td>
                            <td className="px-5 py-3 text-accent-700 font-medium">{row.oopbuy}</td>
                            <td className="px-5 py-3 text-foreground-500">{row.others}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}

            {/* FAQ */}
            {article.faqs.length > 0 && (
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                  {t('articleDetail.faq_title')}
                </h2>
                <div className="space-y-3">
                  {article.faqs.map((faq, i) => (
                    <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200 transition-colors">
                        <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                        <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                        <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <div className="bg-accent-50 rounded-2xl p-8 text-center mt-10">
              <h3 className="text-xl font-heading font-semibold text-foreground-950 mb-3">
                {t('articleDetail.cta_main_title')}
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                {t('articleDetail.cta_main_text')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                {t('articleDetail.cta_main_button')}
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            {/* Back link */}
            <div className="border-t border-background-200 pt-8 mt-8">
              <p className="text-xs text-foreground-400 mb-3">{t('articleDetail.back_label')}</p>
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line"></i>
                {t('articleDetail.back_home')}
              </Link>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="border-t border-background-200 pt-8 mt-4">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">{t('articleDetail.related_title')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="block p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                    >
                      <span className="text-xs text-accent-600 font-medium whitespace-nowrap">{rel.category}</span>
                      <h4 className="text-sm font-medium text-foreground-800 mt-1.5 mb-2 line-clamp-2 group-hover:text-foreground-950 transition-colors">{rel.title}</h4>
                      <span className="text-xs text-foreground-400">{rel.readingTime} Lesezeit</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}